<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useElementSize } from "@vueuse/core";

import { useSnackStore } from "@/stores/snackStore";
import BaseDialogActions from "@/components/shared/dialogs/BaseDialogActions.vue";

const props = withDefaults(
  defineProps<{
    file: File | null;
    aspectRatio?: number;
    maxDisplayWidth?: number;
    maxDisplayHeight?: number;
  }>(),
  {
    aspectRatio: 1,
    maxDisplayWidth: 420,
    maxDisplayHeight: 420,
  }
);

const emit = defineEmits<{
  (e: "save", payload: { file: File }): void;
  (e: "cancel"): void;
}>();

const { t } = useI18n();
const snackStore = useSnackStore();

const MIN_CROP_SIZE = 48;
const JPEG_WEBP_QUALITY = 0.95;

type TRect = { x: number; y: number; width: number; height: number };
type TDragMode = "move" | "nw" | "ne" | "sw" | "se" | "n" | "s" | "e" | "w" | null;
type TResizeHandle = Exclude<TDragMode, "move" | null>;

const CORNER_MODES = new Set<TDragMode>(["nw", "ne", "sw", "se"]);

const HANDLE_BAR_CLASS = "tw:pointer-events-none tw:bg-white tw:shadow-[0_0_0_1px_rgba(0,0,0,0.6)]";

const CROP_HANDLES: { mode: TResizeHandle; wrapper: string; bars: string[] }[] = [
  {
    mode: "nw",
    wrapper:
      "tw:top-0 tw:left-0 tw:size-5 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:cursor-nwse-resize",
    bars: [
      "tw:absolute tw:top-1/2 tw:left-1/2 tw:h-1 tw:w-3 tw:-translate-y-1/2",
      "tw:absolute tw:top-1/2 tw:left-1/2 tw:h-3 tw:w-1 tw:-translate-x-1/2",
    ],
  },
  {
    mode: "ne",
    wrapper:
      "tw:top-0 tw:right-0 tw:size-5 tw:translate-x-1/2 tw:-translate-y-1/2 tw:cursor-nesw-resize",
    bars: [
      "tw:absolute tw:top-1/2 tw:right-1/2 tw:h-1 tw:w-3 tw:-translate-y-1/2",
      "tw:absolute tw:top-1/2 tw:left-1/2 tw:h-3 tw:w-1 tw:-translate-x-1/2",
    ],
  },
  {
    mode: "sw",
    wrapper:
      "tw:bottom-0 tw:left-0 tw:size-5 tw:-translate-x-1/2 tw:translate-y-1/2 tw:cursor-nesw-resize",
    bars: [
      "tw:absolute tw:top-1/2 tw:left-1/2 tw:h-1 tw:w-3 tw:-translate-y-1/2",
      "tw:absolute tw:bottom-1/2 tw:left-1/2 tw:h-3 tw:w-1 tw:-translate-x-1/2",
    ],
  },
  {
    mode: "se",
    wrapper:
      "tw:right-0 tw:bottom-0 tw:size-5 tw:translate-x-1/2 tw:translate-y-1/2 tw:cursor-nwse-resize",
    bars: [
      "tw:absolute tw:top-1/2 tw:right-1/2 tw:h-1 tw:w-3 tw:-translate-y-1/2",
      "tw:absolute tw:bottom-1/2 tw:left-1/2 tw:h-3 tw:w-1 tw:-translate-x-1/2",
    ],
  },
  {
    mode: "n",
    wrapper:
      "tw:top-0 tw:left-1/2 tw:flex tw:h-4 tw:w-8 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:cursor-ns-resize tw:items-center tw:justify-center",
    bars: ["tw:h-1 tw:w-4 tw:rounded-xs"],
  },
  {
    mode: "s",
    wrapper:
      "tw:bottom-0 tw:left-1/2 tw:flex tw:h-4 tw:w-8 tw:-translate-x-1/2 tw:translate-y-1/2 tw:cursor-ns-resize tw:items-center tw:justify-center",
    bars: ["tw:h-1 tw:w-4 tw:rounded-xs"],
  },
  {
    mode: "w",
    wrapper:
      "tw:top-1/2 tw:left-0 tw:flex tw:h-8 tw:w-4 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:cursor-ew-resize tw:items-center tw:justify-center",
    bars: ["tw:h-4 tw:w-1 tw:rounded-xs"],
  },
  {
    mode: "e",
    wrapper:
      "tw:top-1/2 tw:right-0 tw:flex tw:h-8 tw:w-4 tw:translate-x-1/2 tw:-translate-y-1/2 tw:cursor-ew-resize tw:items-center tw:justify-center",
    bars: ["tw:h-4 tw:w-1 tw:rounded-xs"],
  },
];

const editorImageUrl = ref<string | null>(null);
const editorImage = ref<HTMLImageElement | null>(null);
const imageDimensions = ref({ width: 0, height: 0 });
const cropRect = ref<TRect>({ x: 0, y: 0, width: 0, height: 0 });
const isSaving = ref(false);

let dragMode: TDragMode = null;
let dragStartMouse = { x: 0, y: 0 };
let dragStartRect: TRect = { x: 0, y: 0, width: 0, height: 0 };

let hasUserAdjustedCrop = false;

const stageBoxRef = ref<HTMLElement | null>(null);
const { width: measuredBoxWidth, height: measuredBoxHeight } = useElementSize(stageBoxRef);

const boxSize = computed(() => ({
  width: measuredBoxWidth.value || props.maxDisplayWidth,
  height: measuredBoxHeight.value || props.maxDisplayHeight,
}));

const displaySize = computed(() => {
  const { width, height } = imageDimensions.value;
  const { width: bw, height: bh } = boxSize.value;
  if (!width || !height) {
    return { width: bw, height: bh };
  }

  const scale = Math.min(bw / width, bh / height);
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
});

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function failEditor() {
  snackStore.showError(t("generic.errors.errUploadingFile"));
  emit("cancel");
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", () => reject(new Error(src)));
    img.src = src;
  });
}

function defaultCropRect(): TRect {
  const { width: dw, height: dh } = displaySize.value;
  const ratio = props.aspectRatio;

  const width = ratio >= dw / dh ? dw : dh * ratio;
  const height = width / ratio;

  return {
    x: (dw - width) / 2,
    y: (dh - height) / 2,
    width,
    height,
  };
}

function clampRectToBounds(rect: TRect, ratio: number): TRect {
  const { width: dw, height: dh } = displaySize.value;

  let width = clamp(rect.width, MIN_CROP_SIZE, dw);
  let height = width / ratio;

  if (height > dh) {
    height = dh;
    width = height * ratio;
  }

  if (height < MIN_CROP_SIZE) {
    height = MIN_CROP_SIZE;
    width = height * ratio;
  }

  return {
    x: clamp(rect.x, 0, dw - width),
    y: clamp(rect.y, 0, dh - height),
    width,
    height,
  };
}

function releaseImageUrl() {
  if (editorImageUrl.value) {
    URL.revokeObjectURL(editorImageUrl.value);
    editorImageUrl.value = null;
  }
}

async function initEditor() {
  releaseImageUrl();
  editorImage.value = null;

  if (!props.file) return;

  const url = URL.createObjectURL(props.file);

  try {
    const img = await loadImage(url);

    editorImageUrl.value = url;
    editorImage.value = img;
    imageDimensions.value = { width: img.naturalWidth, height: img.naturalHeight };
    cropRect.value = defaultCropRect();
    hasUserAdjustedCrop = false;
  } catch {
    URL.revokeObjectURL(url);
    failEditor();
  }
}

watch(
  () => props.file,
  () => {
    void initEditor();
  },
  { immediate: true }
);

watch(boxSize, () => {
  if (!editorImage.value) return;

  cropRect.value = hasUserAdjustedCrop
    ? clampRectToBounds(cropRect.value, cropRect.value.width / cropRect.value.height)
    : defaultCropRect();
});

function onHandlePointerDown(mode: TDragMode, e: PointerEvent) {
  e.preventDefault();
  dragMode = mode;
  dragStartMouse = { x: e.clientX, y: e.clientY };
  dragStartRect = { ...cropRect.value };
  globalThis.addEventListener("pointermove", onPointerMove);
  globalThis.addEventListener("pointerup", onPointerUp);
}

function onPointerMove(e: PointerEvent) {
  if (!dragMode) return;

  hasUserAdjustedCrop = true;

  const dx = e.clientX - dragStartMouse.x;
  const dy = e.clientY - dragStartMouse.y;
  const { width: dw, height: dh } = displaySize.value;

  if (dragMode === "move") {
    cropRect.value = {
      ...dragStartRect,
      x: clamp(dragStartRect.x + dx, 0, dw - dragStartRect.width),
      y: clamp(dragStartRect.y + dy, 0, dh - dragStartRect.height),
    };
    return;
  }

  const start = dragStartRect;
  const ratio = start.height > 0 ? start.width / start.height : props.aspectRatio;
  const right = start.x + start.width;
  const bottom = start.y + start.height;

  const next: TRect = { ...start };

  const widthFromDx = (signedDx: number) => start.width + signedDx;
  const widthFromDy = (signedDy: number) => (start.height + signedDy) * ratio;

  switch (dragMode) {
    case "e": {
      next.width = clamp(start.width + dx, MIN_CROP_SIZE, dw - start.x);
      break;
    }
    case "w": {
      next.width = clamp(start.width - dx, MIN_CROP_SIZE, right);
      next.x = right - next.width;
      break;
    }
    case "s": {
      next.height = clamp(start.height + dy, MIN_CROP_SIZE, dh - start.y);
      break;
    }
    case "n": {
      next.height = clamp(start.height - dy, MIN_CROP_SIZE, bottom);
      next.y = bottom - next.height;
      break;
    }
    case "se": {
      next.width = Math.abs(dx) > Math.abs(dy) * ratio ? widthFromDx(dx) : widthFromDy(dy);
      next.height = next.width / ratio;
      next.x = start.x;
      next.y = start.y;
      break;
    }
    case "ne": {
      next.width = Math.abs(dx) > Math.abs(dy) * ratio ? widthFromDx(dx) : widthFromDy(-dy);
      next.height = next.width / ratio;
      next.x = start.x;
      next.y = bottom - next.height;
      break;
    }
    case "sw": {
      next.width = Math.abs(dx) > Math.abs(dy) * ratio ? widthFromDx(-dx) : widthFromDy(dy);
      next.height = next.width / ratio;
      next.x = right - next.width;
      next.y = start.y;
      break;
    }
    case "nw": {
      next.width = Math.abs(dx) > Math.abs(dy) * ratio ? widthFromDx(-dx) : widthFromDy(-dy);
      next.height = next.width / ratio;
      next.x = right - next.width;
      next.y = bottom - next.height;
      break;
    }
    default:
      break;
  }

  if (CORNER_MODES.has(dragMode)) {
    cropRect.value = clampRectToBounds(next, ratio);
    return;
  }

  cropRect.value = {
    x: clamp(next.x, 0, dw - next.width),
    y: clamp(next.y, 0, dh - next.height),
    width: clamp(next.width, MIN_CROP_SIZE, dw),
    height: clamp(next.height, MIN_CROP_SIZE, dh),
  };
}

function onPointerUp() {
  dragMode = null;
  globalThis.removeEventListener("pointermove", onPointerMove);
  globalThis.removeEventListener("pointerup", onPointerUp);
}

function resetCrop() {
  cropRect.value = defaultCropRect();
  hasUserAdjustedCrop = false;
}

/** Sub-pixel tolerance so rounding during drag doesn't leave the crop looking "modified". */
const CROP_DIRTY_EPSILON = 0.5;

/** Reset is only offered once the crop actually differs from its initial framing. */
const isCropModified = computed(() => {
  if (!editorImage.value) return false;

  const rect = cropRect.value;
  const initial = defaultCropRect();

  return (
    Math.abs(rect.x - initial.x) > CROP_DIRTY_EPSILON ||
    Math.abs(rect.y - initial.y) > CROP_DIRTY_EPSILON ||
    Math.abs(rect.width - initial.width) > CROP_DIRTY_EPSILON ||
    Math.abs(rect.height - initial.height) > CROP_DIRTY_EPSILON
  );
});

async function onSave() {
  const img = editorImage.value;

  if (!props.file || !img) {
    failEditor();
    return;
  }

  isSaving.value = true;

  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      failEditor();
      return;
    }

    const scale = displaySize.value.width / img.naturalWidth;
    const rect = cropRect.value;

    canvas.width = Math.round(rect.width / scale);
    canvas.height = Math.round(rect.height / scale);

    ctx.drawImage(
      img,
      rect.x / scale,
      rect.y / scale,
      rect.width / scale,
      rect.height / scale,
      0,
      0,
      canvas.width,
      canvas.height
    );

    const mimeType = props.file.type || "image/png";

    const quality =
      mimeType === "image/jpeg" || mimeType === "image/jpg" || mimeType === "image/webp"
        ? JPEG_WEBP_QUALITY
        : undefined;

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, mimeType, quality);
    });

    if (!blob) {
      failEditor();
      return;
    }

    emit("save", {
      file: new File([blob], props.file.name, { type: mimeType, lastModified: Date.now() }),
    });
  } catch {
    failEditor();
  } finally {
    isSaving.value = false;
  }
}

onBeforeUnmount(() => {
  releaseImageUrl();
  globalThis.removeEventListener("pointermove", onPointerMove);
  globalThis.removeEventListener("pointerup", onPointerUp);
});
</script>

<template>
  <div class="tw:flex tw:h-full tw:flex-col tw:items-center tw:relative tw:gap-2">
    <v-btn
      v-if="isCropModified"
      class="tw:absolute tw:right-1 tw:top-2 tw:z-10"
      size="x-small"
      color="white"
      icon="mdi-restore"
      @click="resetCrop"
    />
    <div
      ref="stageBoxRef"
      class="tw:relative tw:flex tw:w-full tw:touch-none tw:items-center tw:justify-center tw:overflow-hidden tw:bg-black/10 tw:select-none"
      :style="{
        maxWidth: `${maxDisplayWidth}px`,
        aspectRatio: `${maxDisplayWidth} / ${maxDisplayHeight}`,
      }"
    >
      <div
        class="tw:relative"
        :style="{ width: `${displaySize.width}px`, height: `${displaySize.height}px` }"
      >
        <img
          v-if="editorImageUrl"
          :src="editorImageUrl"
          alt=""
          class="tw:pointer-events-none tw:h-full tw:w-full tw:select-none"
          draggable="false"
        />

        <div
          class="tw:absolute tw:cursor-move tw:touch-none tw:border-2 tw:border-dashed tw:border-white"
          :style="{
            left: `${cropRect.x}px`,
            top: `${cropRect.y}px`,
            width: `${cropRect.width}px`,
            height: `${cropRect.height}px`,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
          }"
          @pointerdown="onHandlePointerDown('move', $event)"
        >
          <div
            v-for="handle in CROP_HANDLES"
            :key="handle.mode"
            class="tw:absolute"
            :class="handle.wrapper"
            @pointerdown.stop="onHandlePointerDown(handle.mode, $event)"
          >
            <div
              v-for="bar in handle.bars"
              :key="bar"
              :class="[HANDLE_BAR_CLASS, bar]"
            />
          </div>
        </div>
      </div>
    </div>

    <BaseDialogActions
      align="center"
      class="tw:mt-auto tw:w-full"
      :secondary="{
        text: t('generic.action.cancel'),
        action: () => emit('cancel'),
      }"
      :primary="{
        icon: 'mdi-content-save-outline',
        text: t('generic.action.save'),
        loading: isSaving,
        action: onSave,
      }"
    />
  </div>
</template>
